'use client';

import { useAiUsageRemaining } from '@/hooks/useAiUsageRemaining';
import { formatCreditExpiry } from '@/lib/credits/format-credit-expiry';
import { cn } from '@/lib/utils';

type AiUsageRemainingBadgeProps = {
  className?: string;
  compact?: boolean;
};

function resolveUsagePrefix(compact: boolean, ledger: boolean): string {
  if (ledger) return '残り回数';
  return compact ? '残り録音回数' : '今月の残り録音回数';
}

/** 共通枠の組織だけ、実行前に消費量を控えめに添える（文字起こしは 1 回。ADR-0044 Decision 6） */
const TRANSCRIPTION_CREDIT_COST = 1;

function formatLedgerLabel(usage: ReturnType<typeof useAiUsageRemaining>, prefix: string): string {
  const cost = `この操作で ${TRANSCRIPTION_CREDIT_COST} 回消費します`;
  if (usage.isUnlimited) return `${prefix}: 無制限・${cost}`;
  if (typeof usage.remaining !== 'number') return `${prefix}: -`;
  const expiry = usage.nearestExpiresAt ? `（${formatCreditExpiry(usage.nearestExpiresAt)} まで）` : '';
  return `${prefix}: ${usage.remaining} 回${expiry}・${cost}`;
}

function formatUsageLabel(params: { compact: boolean; usage: ReturnType<typeof useAiUsageRemaining> }): string {
  const { compact, usage } = params;
  const prefix = resolveUsagePrefix(compact, usage.creditMode === 'ledger');

  if (usage.loading) {
    return `${prefix}: 取得中...`;
  }

  if (usage.isError) {
    return `${prefix}: 取得失敗`;
  }

  if (usage.creditMode === 'ledger') {
    return formatLedgerLabel(usage, prefix);
  }

  if (usage.isUnlimited) {
    return `${prefix}: 無制限`;
  }

  if (typeof usage.remaining !== 'number' || typeof usage.limit !== 'number') {
    return `${prefix}: -`;
  }

  return `${prefix}: ${usage.remaining} / ${usage.limit}`;
}

export function AiUsageRemainingBadge({ className, compact = false }: AiUsageRemainingBadgeProps) {
  const usage = useAiUsageRemaining();

  if (!usage.hasOrganization || usage.isError) {
    return null;
  }

  const label = formatUsageLabel({ compact, usage });

  return <p className={cn('text-xs text-muted-foreground', className)}>{label}</p>;
}
